import React from 'react';
import { FormattedMessage } from 'react-intl';

import { TableCell, TableRow, IconButton, Tooltip } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';

import { useCivilStatusSelectItems } from '../../../../../../hooks';
import { useGenderSelectItems } from '../../../../../../hooks';

import EventContentTable from '../EventContentTable';
import { useEventDifferences } from '../EventContentTable/EventContentTable';

const CHANGE_STYLES = {
  backgroundColor: '#fff3e0',
  color: '#ef6c00',
  fontWeight: 'bold',
};

type EventContentTableRowProps = {
  header: string;
  value: any;
  renderer?: (value: any, header: string) => any;
  hasChanged?: boolean;
};

export const EventContentTableRow = ({ header, value, renderer, hasChanged = false }: EventContentTableRowProps) => {
  const { getGenderDisplayNameByValue } = useGenderSelectItems();
  const { getCivilStatusDisplayNameByValue } = useCivilStatusSelectItems();
  const eventDifferences = useEventDifferences();

  // Helper function to format values
  const formatValue = React.useCallback(
    (val: any, includeNestedObjects: boolean = false) => {
      if (isNullish(val)) {
        return '';
      }

      if (typeof renderer === 'function') {
        return renderer(val, header);
      }

      if (val && typeof val === 'object') {
        return includeNestedObjects ? (
          <EventContentTable event={val} ignoreKeys={['@key', '@type', '@id']} collapse />
        ) : (
          JSON.stringify(val)
        );
      }

      switch (header) {
        case 'gender':
          return getGenderDisplayNameByValue(val);
        case 'civilStatus':
          return getCivilStatusDisplayNameByValue(val);
        case 'state':
          return <FormattedMessage id={val} />;
        case 'employmentType':
          return <FormattedMessage id={`employmentType.${val}`} />;
        case 'employmentContractType':
          return <FormattedMessage id={`global.contractType.${val}`} />;
        case 'employeeCategory':
          return <FormattedMessage id={`employees.detail.employeeCategory.${val}`} />;
        default:
          return val;
      }
    },
    [header, renderer, getGenderDisplayNameByValue, getCivilStatusDisplayNameByValue],
  );

  // Get previous value for hover display
  const previousValue = React.useMemo(() => {
    if (!hasChanged || !eventDifferences) return null;

    const matchingKey = Object.keys(eventDifferences).find(
      diffKey => diffKey.endsWith(`.${header}`) || diffKey === header,
    );

    return matchingKey ? eventDifferences[matchingKey] : null;
  }, [hasChanged, eventDifferences, header]);

  // Format previous value using the same logic as current value
  const formattedPreviousValue = React.useMemo(() => {
    if (previousValue === null) return null;
    return formatValue(previousValue, false);
  }, [previousValue, formatValue]);

  const currentValue = React.useMemo(() => {
    return formatValue(value, true);
  }, [value, formatValue]);

  const cellStyle = React.useMemo(() => (hasChanged ? CHANGE_STYLES : {}), [hasChanged]);

  const headerContent = React.useMemo(
    () => (/^\d+$/.test(header) ? header : <FormattedMessage id={`events.content.header.${header}`} />),
    [header],
  );

  return (
    <TableRow>
      <TableCell variant="head" sx={{ width: '25%', ...cellStyle }}>
        {headerContent}
        {hasChanged && (
          <Tooltip title={formattedPreviousValue || 'No previous value'} placement="right" arrow>
            <IconButton size="small" sx={{ verticalAlign: 'sub' }}>
              {formattedPreviousValue ? <ChangeCircleIcon fontSize="small" /> : <AddCircleIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
      <TableCell sx={{ width: '75%', ...cellStyle }}>{currentValue}</TableCell>
    </TableRow>
  );
};

const isNullish = (val: any) => val === null || val === undefined || val === '';
